import React, { useMemo, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import qs from 'qs';
import { useMedia } from 'react-use';
import { Tabs, Tab, Typography, StickyBar, Icon } from 'ck-ui';
import { handleize } from 'ck-utils';
import { sanityClient } from 'src/sanityClient';
import { SizeChartContent } from './SizeChartContent';
import { Label } from '../ProductListingPage/utils';
import styles from './NewSizeChart.module.scss';

const NewSizeChart = (props = {}) => {
  const isMobile = useMedia('(max-width: 767px)');
  const params = useMemo(() => {
    const queryString = window.location.search.substr(1);
    return props?.params || (queryString ? qs.parse(queryString) : {});
  }, [props?.params]);
  const brand = useMemo(() => params.brand || window.location.pathname.split('/').pop().split('-')[0], [params]);
  const sizeChart = props[brand] || {};
  const genders = ['mens', 'womens', 'kids'].filter(gender => sizeChart[gender]);
  const [selectedGender, setSelectedGender] = useState(params.gender || genders[0]);
  const [selectedCategory, setSelectedCategory] = useState(params.category || sizeChart[selectedGender]?.[0].category);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const sizeChartRefs = useRef({});

  const scrollToAnchor = (anchorName) => {
    const element = sizeChartRefs.current[anchorName];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    const keys = Object.keys(sizeChartRefs.current);
    if (params.gender && params.category && keys?.length > 0) {
      scrollToAnchor(`${params.gender}-${params.category}`);
    }
  }, [params.category, params.gender]);

  if (navigator.userAgent === 'ReactSnap') return null;

  const getHeader = () => (
    <div className={classnames(styles.header, { container: !params.noLayout })}>
      <Typography
        uppercase
        variant={isMobile ? 'heading6' : 'heading2'}
        display="block"
        align="centered"
        className={styles.heading}
      >
        SIZING INFORMATION
      </Typography>
      <Tabs
        value={selectedGender}
        className={styles.tabs}
        onChange={(e, value) => {
          if (selectedGender !== value) {
            setSelectedGender(value);
            if (sizeChart[value].length > 0) {
              const firstCategory = sizeChart[value][0].category;
              setSelectedCategory(firstCategory);
              scrollToAnchor(`${value}-${firstCategory}`);
            }
          }
        }}
      >
        {genders.map(gender => (
          <Tab
            key={gender}
            value={gender}
            className={styles.tab}
          >
            {gender}
          </Tab>
        ))}
      </Tabs>
      <div className={styles.nav}>
        <div className={classnames(styles.selector, { [styles.collapsed]: !dropdownOpen })} aria-label="dropdown" aria-haspopup="listbox">
          {sizeChart[selectedGender].map((item) => {
            const isSubcategory = item.category.includes('--');
            const category = isSubcategory ? item.category.split('--')[1] : item.category.replace('category-', '');
            const active = selectedCategory === item.category;
            return (
              <Typography
                key={item._key}
                renderAs="button"
                uppercase
                variant={isMobile ? 'body-small' : 'body-medium'}
                className={classnames(
                  styles.navItem,
                  { [styles.active]: active,
                    'is-hidden': !dropdownOpen && !active })}
                weight={700}
                onClick={() => {
                  if (dropdownOpen) {
                    setSelectedCategory(item.category);
                    scrollToAnchor(`${item.gender}-${item.category}`);
                  }
                  setDropdownOpen(!dropdownOpen);
                }}
              >
                <Icon size={isMobile ? 'large' : 'exLarge'} name={Label.getIconCode(category, item.gender)} />
                <span className={styles.category}>
                  {dropdownOpen ? Label.getLocaleString(category, isSubcategory ? 'subcategory' : 'category') : 'CLICK TO EXPAND'}
                </span>
              </Typography>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className={params.noLayout ? styles.modal : styles.page}>
      {params.noLayout ? getHeader() : (
        <StickyBar>
          {getHeader()}
        </StickyBar>
      )}

      <div className={params.noLayout ? styles.modalContent : 'container'}>
        {genders.map(gender => sizeChart[gender].map(item => {
          if (item.isUniSex && !item.table) {
            item.table = sizeChart.mens.find(ele => ele.category === item.category)?.table;
          }
          const id = `${item.gender}-${item.category}`;
          return (
            <div key={item._key} className="is-relative">
              {/* eslint-disable-next-line no-return-assign */}
              <span className={params.noLayout ? null : styles.anchorWithFixedHeader} ref={ele => sizeChartRefs.current[id] = ele} />
              <SizeChartContent item={item} />
            </div>
          );
        }))}
      </div>
    </div>
  );
};

NewSizeChart.propTypes = {
  params: PropTypes.object
};

const getStaticProps = async () => {
  const result = {};
  const fragment = `{
      brand->{title},
      "mens": mens[]{"category": categoryItem->title, _key, "gender": genderItem->title, title, isUniSex,noSwitch,table,tips,image},
      "kids": kids[]{"category": categoryItem->title, _key, "gender": genderItem->title, title, isUniSex,noSwitch,table,tips,image},
      "womens": womens[]{"category": categoryItem->title, _key, "gender": genderItem->title, title, isUniSex,noSwitch,table,tips,image}
    }`;
  const data = await sanityClient.fetch(
    `*[_type == "sizeChart"]${fragment}`
  );

  data.forEach((item) => {
    result[handleize(item.brand.title)] = {
      mens: item.mens,
      womens: item.womens,
      kids: item.kids,
    };
  });
  return result;
};

export { NewSizeChart, getStaticProps };
